import React, { useEffect, useMemo, useState } from "react";
import "./DashboardContent.css";
import { v4 as uuidv4 } from "uuid";
import { notification } from "antd";
import { useNavigate, useParams } from "react-router-dom";
import { useDrop } from "react-dnd";
import { WidgetCard } from "../WidgetCard/WidgetCard";
import { useGetDashboardListQuery } from "../../../store/api/lumi/dashboard";

import {
    useGetConceptMetaDataQuery,
    useGetConceptsListQuery,
    useGetConceptsOptionsMutation,
} from "../../../store/api/lumi/concepts";
import { useGetProjectByIdQuery } from "../../../store/api/lumi/projects";
import GridLayout from "react-grid-layout";
import { validateAndRepairLayout } from "../../../utils/validateAndRepairLayout";
import { isConceptTypeValid } from "../../../utils/dashboardStatus";
import { useDispatch, useSelector } from "react-redux";
import {
    loadDashboard,
    saveToLocalStorage,
    setActiveConceptList,
    setSelections,
    setTopConcepts,
} from "../../../store/slices/dashboardSlice";
import {
    setSentimentDisabled,
    setTopOptionsConcepts,
    setTotalDocCount,
} from "../../../store/slices/projectSlice";
import { selectDashboardById } from "../../../store/selectors/selectors";

export const DashboardContent = ({ isSidebarOpen, width = "220px" }) => {
    const containerStyle = {
        backgroundColor: "#eeeeee",
        marginLeft: isSidebarOpen ? width : "0",
        width: isSidebarOpen ? `calc(100% - ${width})` : "100%",
    };
    const { id, dashboardName: nameFromParams } = useParams();
    const {
        topFilters = [],
        selections = {},
        topConcepts = [],
        topConceptList = "",
        topConceptListDaylight = "",
        activeConceptList = "",
    } = useSelector((state) => selectDashboardById(state, id));

    const { data: dashboardList } = useGetDashboardListQuery(id);
    const { data: dataList, isLoading } = useGetConceptsListQuery(id);
    const { data: project, isFetching } = useGetProjectByIdQuery(id);
    const { data: metaDataQu, isSuccess } = useGetConceptMetaDataQuery(id);

    const dashboardState = useSelector((state) =>
        selectDashboardById(state, id)
    );

    const dispatch = useDispatch();
    const navigate = useNavigate();

    useEffect(() => {
        dispatch(loadDashboard(id));
    }, [id, dispatch, dashboardState]);

    useEffect(() => {
        if (dashboardState[id]) {
            dispatch(saveToLocalStorage(id));
        }
    }, [id, dispatch, dashboardState]);
    useEffect(() => {
        if (project?.last_build_info?.sentiment?.skip_sentiment) {
            dispatch(setSentimentDisabled(true));
        }
    }, [project, dispatch]);
    useEffect(() => {
        if (!isFetching && !project) {
            navigate("/404", { replace: true });
        }
    }, [project, isFetching, navigate]);
    const [api, contextHolder] = notification.useNotification();
    const [currentDashboard, setCurrentDashboard] = useState("");
    const [getConceptOptionMut] = useGetConceptsOptionsMutation();
    const [reset, setReset] = useState(false);

    const initialState = () => {
        const savedState = localStorage.getItem(`dashboardState-${id}`);
        if (savedState) {
            const parsedState = JSON.parse(savedState);

            const needsRepair = parsedState.openedWidgets.some((widget) => {
                if (!widget.layout) {
                    localStorage.removeItem(`dashboardState-${id}`);
                    return;
                }
                validateAndRepairLayout(widget.layout);
            });
            if (needsRepair) {
                localStorage.setItem(
                    `dashboardState-${id}`,
                    JSON.stringify(parsedState)
                );
                console.warn(
                    "Some widget layouts were repaired and updated in local storage."
                );
            }
            return parsedState;
        }
        return {
            openedWidgets: [],
            topFilters: [],
            topConcepts: [],
            selections: {},
            activeConceptList: "top",
        };
    };

    const [openedWidgets, setOpenedWidgets] = useState(
        initialState().openedWidgets
    );

    useEffect(() => {
        const dashboardState = {
            openedWidgets: openedWidgets,
            topFilters: topFilters,
            topConcepts: topConcepts,
            selections: selections,
            activeConceptList:
                topConceptList || topConceptListDaylight || activeConceptList,
        };

        localStorage.setItem(
            `dashboardState-${id}`,
            JSON.stringify(dashboardState)
        );
    }, [openedWidgets, topFilters, topConcepts, id, selections]);

    useEffect(() => {
        let conceptFilter = { type: "top", limit: 50 };
        if (topConceptList) {
            conceptFilter = {
                type: "concept_list",
                concept_list_id: topConceptList,
            };
        }
        getConceptOptionMut({
            id,
            concept_selector: conceptFilter,
        }).then(({ data }) => {
            dispatch(setTotalDocCount(data.total_count));
            dispatch(setTopOptionsConcepts(data.result));
        });
    }, [getConceptOptionMut, id, topConceptList]);

    const [placeholder, setPlaceholder] = useState(null);

    const [{ isOver, canDrop, item, clientOffset }, drop] = useDrop({
        accept: "chart",
        drop: (item, monitor) => {
            const { text, gridData } = item;
            const newWidgetId = uuidv4();
            const { w = 7, h = 13, minH = 3, minW = 3 } = gridData;
            const offset = monitor.getClientOffset();
            const dropTarget = document.querySelector(".react-grid-layout");
            const targetRect = dropTarget.getBoundingClientRect();
            const x = Math.floor((offset.x - targetRect.left) / 100);
            const y = Math.floor((offset.y - targetRect.top) / 30);
            setOpenedWidgets((prevWidgets) => [
                ...prevWidgets,
                {
                    id: newWidgetId,
                    text,
                    gridData,
                    layout: { i: newWidgetId, x, y, w, h, minH, minW },
                },
            ]);
            setPlaceholder(null);
        },
        hover: (item, monitor) => {
            const offset = monitor.getClientOffset();
            const dropTarget = document.querySelector(".react-grid-layout");
            const targetRect = dropTarget.getBoundingClientRect();
            const x = Math.floor((offset.x - targetRect.left) / 100);
            const y = Math.floor((offset.y - targetRect.top) / 30);
            const { w = 7, h = 13 } = item.gridData;
            setPlaceholder({ x, y, w, h });
        },
        collect: (monitor) => ({
            isOver: !!monitor.isOver(),
            canDrop: !!monitor.canDrop(),
            item: monitor.getItem(),
            clientOffset: monitor.getClientOffset(),
        }),
    });

    const renderPlaceholder = () => {
        if (placeholder) {
            const { x, y, w, h } = placeholder;
            return (
                <div
                    style={{
                        position: "absolute",
                        left: `${x * 100}px`,
                        top: `${y * 30}px`,
                        width: `${w * 100}px`,
                        height: `${h * 40}px`,
                        backgroundColor: "rgba(0, 0, 255, 0.2)",
                        border: "1px dashed blue",
                        pointerEvents: "none",
                    }}
                />
            );
        }
        return null;
    };

    const handleWidgetStateChange = (widgetId, newState) => {
        setOpenedWidgets((currentWidgets) =>
            currentWidgets.map((widget) =>
                widget.id === widgetId
                    ? { ...widget, dataProps: newState }
                    : widget
            )
        );
    };

    const widgets = useMemo(() => {
        if (!isSuccess && isLoading) {
            return null;
        }

        return openedWidgets.map((widget, idx) => (
            <div
                id={widget.id}
                key={widget?.id}
                data-grid={widget.layout}
                className="grid-item"
            >
                <WidgetCard
                    reset={reset}
                    setReset={setReset}
                    project={project}
                    setter={setOpenedWidgets}
                    data={openedWidgets}
                    widget={widget}
                    metaDataQu={metaDataQu}
                    isSuccess={isSuccess}
                    key={widget?.id}
                    onWidgetStateChange={handleWidgetStateChange}
                    idx={idx}
                    dataList={dataList}
                />
            </div>
        ));
    }, [openedWidgets, isSuccess, metaDataQu]);

    useEffect(() => {
        if (currentDashboard?.dashboard_id) {
            setOpenedWidgets(
                Array.isArray(currentDashboard?.charts)
                    ? [...currentDashboard.charts]
                    : []
            );
            dispatch(
                setTopConcepts({
                    id,
                    concepts: currentDashboard.top_concepts || [],
                })
            );
            dispatch(
                setSelections({
                    id,
                    selections: currentDashboard.selections || {},
                })
            );
            const { active_concept_list } = currentDashboard;
            if (!active_concept_list) {
                dispatch(
                    setActiveConceptList({ id, activeConceptList: "top" })
                );
            } else if (isConceptTypeValid(active_concept_list)) {
                dispatch(
                    setActiveConceptList({
                        id,
                        list: `Type: ${active_concept_list}`,
                    })
                );
            } else {
                dispatch(
                    setActiveConceptList({ id, list: active_concept_list })
                );
            }
        }
    }, [currentDashboard, dispatch, id]);

    const handleDashboardSelection = (dashboardId) => {
        const selectedDashboard = dashboardList.find(
            (dashboard) => dashboard.dashboard_id === dashboardId
        );
        setReset(true);
        if (selectedDashboard) {
            setCurrentDashboard(selectedDashboard);
            const projectId = selectedDashboard.project_id;
            const dashboardNameEncoded = encodeURIComponent(
                selectedDashboard.name
            );
            navigate(
                `/projects/${projectId}/dashboard/${dashboardNameEncoded}`
            );
        } else {
            setCurrentDashboard("");
            navigate(`/projects/${id}/dashboard/`);
        }
    };

    useEffect(() => {
        if (dashboardList && nameFromParams) {
            const decodedName = decodeURIComponent(nameFromParams);
            const dashboardToSelect = dashboardList.find(
                (dashboard) => dashboard.name === decodedName
            );
            if (dashboardToSelect) {
                setCurrentDashboard(dashboardToSelect);
            }
        }
    }, [dashboardList, nameFromParams]);
    const [showFilters, setShowFilters] = useState(false);

    const onLayoutChange = (newLayout) => {
        try {
            setOpenedWidgets((currentWidgets) => {
                return currentWidgets.map((widget) => {
                    const updatedLayout = newLayout.find(
                        (l) => l.i === widget.id
                    );
                    validateAndRepairLayout(updatedLayout);
                    return { ...widget, layout: updatedLayout };
                });
            });
        } catch (error) {
            console.error(
                "Failed to update layout due to invalid data:",
                error.message
            );
        }
    };
    return (
        <div className="main-content-container" style={containerStyle}>
            <div data-testid="dropZone">
                {contextHolder}

                <div ref={drop}>
                    <div className="scroll-content">
                        <GridLayout
                            className="layout"
                            style={{
                                minHeight: `${100 + openedWidgets.length * 60}vh`,
                                minWidth: "100%",
                            }}
                            layout={openedWidgets.map(
                                (widget) => widget.layout
                            )}
                            cols={24}
                            rowHeight={30}
                            width={2400}
                            onLayoutChange={onLayoutChange}
                            isResizable
                            isDraggable
                        >
                            {widgets}
                        </GridLayout>
                        {renderPlaceholder()}
                    </div>
                </div>
            </div>
        </div>
    );
};
